/**
 * Пересчёт склада.
 *
 * Кладовщик ходит вдоль полок с планшетом и вписывает, сколько нашёл.
 * Всё сделано под эту работу: крупные поля под палец, счётчики сверху,
 * фильтры, чтобы вернуться к недосчитанному.
 *
 * Учётный остаток показан рядом с полем — и это осознанный выбор.
 * Прятать его «чтобы не подсказывал» бессмысленно: человек всё равно
 * увидит расхождение после сохранения, а искать полку заново будет
 * втрое дольше.
 */
import { useEffect, useState } from 'react';
import { useAskSheet, useNote } from './ui/ask';
import { useQuery } from '@tanstack/react-query';
import {
  INV_REASONS, formatMoney, invNeedsNote, invTotals,
  type InvReason,
} from '@dastarhan/core';

const API = '/api/v1';

type Line = {
  productId: string;
  name: string;
  unit: string;
  expected: number;
  actual: number | null;
  cost: number;
  reason?: InvReason | null;
  note?: string | null;
};

type Doc = {
  id: string;
  code: string;
  status: 'DRAFT' | 'POSTED' | 'CANCELLED';
  snapshotAt: string;
  postedAt: string | null;
  byName: string;
  items: Line[];
};

const STATUS_RU: Record<Doc['status'], string> = {
  DRAFT: 'Черновик',
  POSTED: 'Проведён',
  CANCELLED: 'Отменён',
};

export function InventoryScreen({ token }: { token: string }) {
  const { ask, sheet } = useAskSheet();
  const { note, noteView } = useNote();
  const h = { Authorization: `Bearer ${token}` };

  /*
   * Точка выбирается так же, как на складе: пересчёт идёт по конкретному
   * заведению, и брать первое попавшееся — верный способ приписать
   * недостачу чужому кафе.
   */
  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const r = await fetch(`${API}/menu/locations`, { headers: h });
      return r.json() as Promise<{ id: string; name: string }[]>;
    },
  });
  const [pickedLoc, setPickedLoc] = useState<string | null>(null);
  const locationId = pickedLoc ?? locations.data?.[0]?.id ?? '';
  const jh = { ...h, 'Content-Type': 'application/json' };

  const [docId, setDocId] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [q, setQ] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [onlyLeft, setOnlyLeft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const list = useQuery({
    queryKey: ['inv-list', locationId],
    queryFn: async () => {
      const r = await fetch(`${API}/stock/inventory-list/${locationId}`, { headers: h });
      return r.json() as Promise<{
        rows: {
          id: string; code: string; status: Doc['status']; at: string;
          byName: string; counted: number; total: number;
          shortageMoney: number; diffMoney: number;
        }[];
      }>;
    },
    enabled: docId === null && !!locationId,
  });

  // Открытый черновик подхватываем сами: человек вернулся после обеда
  // и должен продолжить, а не искать, куда делся его пересчёт
  useEffect(() => {
    if (docId || !list.data) return;
    const draft = list.data.rows.find((r) => r.status === 'DRAFT');
    if (draft) void open(draft.id);
  }, [list.data, docId]);

  const open = async (id: string) => {
    const r = await fetch(`${API}/stock/inventory/${id}`, { headers: h });
    const d = await r.json() as Doc;
    setDoc(d);
    setLines(d.items);
    setDocId(id);
  };

  const start = async () => {
    setErr('');
    const r = await fetch(`${API}/stock/inventory/start`, {
      method: 'POST', headers: jh, body: JSON.stringify({ locationId }),
    });
    const d = await r.json() as { id?: string; docCode?: string; code?: string };
    if (!r.ok) {
      setErr(d.docCode
        ? `Пересчёт ${d.docCode} уже идёт — откройте его`
        : 'Не удалось начать пересчёт');
      if (d.id) void open(d.id);
      return;
    }
    if (d.id) void open(d.id);
  };

  const save = async (next: Line[], quiet = false) => {
    if (!docId) return;
    if (!quiet) setSaving(true);
    try {
      await fetch(`${API}/stock/inventory/${docId}/save`, {
        method: 'POST', headers: jh, body: JSON.stringify({ items: next }),
      });
    } finally {
      if (!quiet) setSaving(false);
    }
  };

  /*
   * Сохраняем сами через пару секунд после последнего касания.
   *
   * Кладовщик не будет жать «сохранить» после каждой полки, а планшет
   * может сесть или потерять сеть. Тихое сохранение стоит одного
   * запроса и снимает целый класс потерь.
   */
  useEffect(() => {
    if (!docId || doc?.status !== 'DRAFT') return;
    const t = setTimeout(() => void save(lines, true), 2000);
    return () => clearTimeout(t);
  }, [lines, docId, doc?.status]);

  const setLine = (productId: string, patch: Partial<Line>) => {
    setLines((prev) => prev.map((l) => (l.productId === productId ? { ...l, ...patch } : l)));
  };

  const post = async () => {
    if (!docId) return;
    setErr('');
    await save(lines);
    const r = await fetch(`${API}/stock/inventory/${docId}/post`, {
      method: 'POST', headers: jh, body: JSON.stringify({ locationId }),
    });
    const d = await r.json() as { reason?: string };
    if (!r.ok) { setErr(d.reason ?? 'Не удалось провести'); return; }
    setDocId(null); setDoc(null); setLines([]);
    void list.refetch();
  };

  const cancel = async () => {
    if (!docId) return;
    const a = await ask({
      title: 'Отменить пересчёт',
      sub: 'Документ останется в истории — по нему будет видно, что считали и когда бросили.',
      effects: [
        ['Введённые цифры', 'сохранятся в истории'],
        ['Остатки склада', 'не изменятся'],
        ['Продолжить позже', 'нельзя — считать придётся заново'],
      ],
      danger: true,
      confirmLabel: 'Отменить пересчёт',
    });
    if (!a) return;
    await fetch(`${API}/stock/inventory/${docId}/cancel`, { method: 'POST', headers: h });
    setDocId(null); setDoc(null); setLines([]);
    void list.refetch();
  };

  // ── список документов ──
  if (!docId) {
    return (
      <section className="inv">
        <header className="inv-head">
          <div>
            <h2>Пересчёт склада</h2>
            {(locations.data?.length ?? 0) > 1 && (
              <select value={locationId}
                onChange={(e) => setPickedLoc(e.target.value)}>
                {locations.data!.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
          </div>
          <button className="btn primary" disabled={!locationId}
            onClick={() => void start()}>
            Начать пересчёт
          </button>
        </header>
        {err && <p className="rb-warn">{err}</p>}

        <p className="hint">
          Учётные остатки фиксируются в момент начала. Всё, что продадут
          во время пересчёта, в расхождение не попадёт.
        </p>

        {list.isPending && <p className="hint">Загружаем…</p>}
        {list.data?.rows.length === 0 && (
          <p className="note">Пересчётов ещё не было.</p>
        )}
        {(list.data?.rows.length ?? 0) > 0 && (
          <table className="grid">
            <thead>
              <tr>
                <th>Документ</th><th>Когда</th><th>Кто</th>
                <th className="num">Пересчитано</th>
                <th className="num">Недостача</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.rows.map((r) => (
                <tr key={r.id} className="inv-row" onClick={() => void open(r.id)}>
                  <td><b>{r.code}</b></td>
                  <td>{new Date(r.at).toLocaleString('ru-RU', {
                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                  })}</td>
                  <td>{r.byName}</td>
                  <td className="num">{r.counted} / {r.total}</td>
                  <td className="num">
                    {r.shortageMoney > 0
                      ? <b className="inv-short">−{formatMoney(r.shortageMoney)}</b>
                      : '—'}
                  </td>
                  <td>
                    <span className={`badge inv-${r.status.toLowerCase()}`}>
                      {STATUS_RU[r.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    );
  }

  // ── сам пересчёт ──
  const t = invTotals(lines);
  const draft = doc?.status === 'DRAFT';

  const shown = lines.filter((l) => {
    if (q && !l.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (onlyLeft && l.actual !== null) return false;
    if (onlyDiff && (l.actual === null || l.actual === l.expected)) return false;
    return true;
  });

  return (
    <section className="inv">
      <header className="inv-head">
        <div>
          <h2>{doc?.code}</h2>
          <span className="hint">
            Снимок {doc && new Date(doc.snapshotAt).toLocaleString('ru-RU', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            })} · {doc?.byName} · {doc && STATUS_RU[doc.status]}
          </span>
        </div>
        <button className="btn" onClick={() => { setDocId(null); setDoc(null); }}>
          ← К списку
        </button>
      </header>

      {/* Счётчики: кладовщик смотрит сюда после каждой полки.
          Излишек и недостача врозь — в сумме они гасят друг друга
          и показали бы ложное «всё сошлось» */}
      <div className="inv-totals">
        <div className="it-card">
          <span>Пересчитано</span>
          <b>{t.counted} / {t.total}</b>
        </div>
        <div className="it-card">
          <span>Излишек</span>
          <b className="it-plus">+{t.surplusQty}</b>
        </div>
        <div className="it-card">
          <span>Недостача</span>
          <b className="it-minus">−{t.shortageQty}</b>
        </div>
        <div className="it-card wide">
          <span>Недостача деньгами</span>
          <b className={t.shortageMoney > 0 ? 'it-minus' : ''}>
            {formatMoney(t.shortageMoney)}
          </b>
        </div>
      </div>

      <div className="inv-tools">
        <input className="field" placeholder="Найти товар"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="check">
          <input type="checkbox" checked={onlyDiff}
            onChange={(e) => setOnlyDiff(e.target.checked)} />
          Только расхождения
        </label>
        <label className="check">
          <input type="checkbox" checked={onlyLeft}
            onChange={(e) => setOnlyLeft(e.target.checked)} />
          Только не пересчитанные
        </label>
        {draft && (
          /* Заполнить фактом по учёту: когда на складе всё сошлось
             и вписывать сотню одинаковых цифр руками незачем */
          <button className="btn small" onClick={() => void (async () => {
            const empty = lines.filter((l) => l.actual === null).length;
            if (empty === 0) { note('Все строки уже посчитаны'); return; }
            const a = await ask({
              title: 'Заполнить факт по учёту',
              sub: 'Подходит, когда пересчитали часть склада, а по остальному расхождений нет.',
              effects: [
                ['Незаполненных строк', String(empty)],
                ['Что произойдёт', 'факт станет равен учёту — расхождения по ним будут нулевые'],
              ],
              confirmLabel: `Заполнить ${empty}`,
            });
            if (!a) return;
            setLines((prev) => prev.map((l) => (l.actual === null ? { ...l, actual: l.expected } : l)));
            note(`Заполнено строк: ${empty}`);
          })()}>
            Заполнить фактом по учёту
          </button>
        )}
      </div>

      {err && <p className="rb-warn">{err}</p>}

      <table className="grid inv-table">
        <thead>
          <tr>
            <th>Товар</th>
            <th className="num">Учёт</th>
            <th className="num">Факт</th>
            <th className="num">Разница</th>
            <th className="num">Сумма</th>
            <th>Причина расхождения</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((l) => {
            const diff = l.actual === null ? null : +(l.actual - l.expected).toFixed(3);
            const money = diff === null || diff === 0 ? 0 : Math.round(Math.abs(diff) * l.cost);
            return (
              <tr key={l.productId} className={diff ? 'inv-diff' : ''}>
                <td>
                  <b>{l.name}</b>
                  <div className="sub">{l.unit}</div>
                </td>
                <td className="num">{l.expected}</td>
                <td className="num">
                  <input className="inv-actual" inputMode="decimal"
                    disabled={!draft}
                    value={l.actual ?? ''}
                    placeholder="—"
                    onChange={(e) => {
                      const raw = e.target.value.replace(',', '.').trim();
                      setLine(l.productId, {
                        actual: raw === '' ? null : Number(raw),
                      });
                    }} />
                </td>
                <td className="num">
                  {diff === null ? '—' : (
                    <b className={diff > 0 ? 'it-plus' : diff < 0 ? 'it-minus' : ''}>
                      {diff > 0 ? '+' : ''}{diff}
                    </b>
                  )}
                </td>
                <td className="num">{money > 0 ? formatMoney(money) : '—'}</td>
                <td>
                  {diff !== null && diff !== 0 ? (
                    <div className="inv-reason">
                      <select value={l.reason ?? ''} disabled={!draft}
                        onChange={(e) => setLine(l.productId, {
                          reason: (e.target.value || null) as InvReason | null,
                        })}>
                        <option value="">— выберите причину —</option>
                        {INV_REASONS.map((r) => (
                          <option key={r.key} value={r.key}>{r.label}</option>
                        ))}
                      </select>
                      {l.reason && invNeedsNote(l.reason) && (
                        <input className="field" placeholder="опишите словами"
                          disabled={!draft}
                          value={l.note ?? ''}
                          onChange={(e) => setLine(l.productId, { note: e.target.value })} />
                      )}
                    </div>
                  ) : <span className="sub">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {shown.length === 0 && (
        <p className="note">
          {onlyDiff ? 'Расхождений нет — всё сошлось.' : 'Ничего не найдено.'}
        </p>
      )}

      {draft && (
        <div className="inv-actions">
          <button className="btn ghost" onClick={() => void cancel()}>Отменить пересчёт</button>
          <button className="btn" disabled={saving} onClick={() => void save(lines)}>
            {saving ? 'Сохраняем…' : 'Сохранить черновик'}
          </button>
          <button className="btn primary" onClick={() => void post()}>
            Провести
          </button>
        </div>
      )}

      {draft && t.needReason > 0 && (
        <p className="hint">
          Расхождений без причины: {t.needReason}. Провести можно только
          после того, как объясните каждое — через месяц никто не вспомнит,
          откуда взялась разница.
        </p>
      )}
      {sheet}
      {noteView}
    </section>
  );
}
